/**
 * Plan horario — Santa Coloma de Gramenet.
 *
 * PLACEHOLDER — esquema de planes provisional. Los tiempos de fase
 * y el calendario horario reales los entregará ARS93 junto con los
 * datos de reguladores. Aquí solo se reutiliza el esquema de cuatro
 * planes (nocturno / base / punta / máxima demanda) para que el
 * demo y el dashboard funcionen.
 *
 * Fases macro de la simulación:
 *   A = F1 + T1 + F2  (eje principal verde)
 *   B = T2             (transición ámbar)
 *   C = F3 + T3 + F4  (eje secundario verde)
 *   D = T4             (transición ámbar)
 */

export interface PlanConfig {
  plan: number;
  label: string;
  cycleTime: number;        // deciseconds
  phaseDurations: number[]; // [A, B, C, D] in deciseconds
}

// F1, F2, F3, F4 variables por plan; T1=1, T2=11, T3=1, T4=15 constantes
export const PLAN_CONFIGS: Record<number, PlanConfig> = {
  1: {
    plan: 1,
    label: 'Nocturno',
    cycleTime: 700,
    phaseDurations: [280, 110, 160, 150],  // A=12+1+15=28s, B=11s, C=14+1+1=16s, D=15s
  },
  2: {
    plan: 2,
    label: 'Base',
    cycleTime: 850,
    phaseDurations: [360, 110, 230, 150],  // A=12+1+23=36s, B=11s, C=14+1+8=23s, D=15s
  },
  3: {
    plan: 3,
    label: 'Hora punta',
    cycleTime: 1000,
    phaseDurations: [450, 110, 290, 150],  // A=12+1+32=45s, B=11s, C=14+1+14=29s, D=15s
  },
  4: {
    plan: 4,
    label: 'Máx. capacidad',
    cycleTime: 1100,
    phaseDurations: [520, 110, 320, 150],  // A=12+1+39=52s, B=11s, C=14+1+17=32s, D=15s
  },
};

// Schedule entry: from this time, activate this plan
interface ScheduleEntry {
  hour: number;
  minute: number;
  plan: number;
}

type DayType = 'weekday' | 'saturday' | 'sunday';

const SCHEDULE: Record<DayType, ScheduleEntry[]> = {
  weekday: [
    { hour: 0, minute: 0, plan: 1 },
    { hour: 6, minute: 0, plan: 2 },
    { hour: 8, minute: 0, plan: 3 },
    { hour: 9, minute: 45, plan: 2 },
    { hour: 13, minute: 15, plan: 3 },
    { hour: 14, minute: 30, plan: 2 },
    { hour: 17, minute: 30, plan: 3 },
    { hour: 18, minute: 15, plan: 4 },
    { hour: 20, minute: 15, plan: 3 },
    { hour: 21, minute: 0, plan: 2 },
  ],
  saturday: [
    { hour: 0, minute: 0, plan: 1 },
    { hour: 7, minute: 0, plan: 2 },
  ],
  sunday: [
    { hour: 0, minute: 0, plan: 1 },
    { hour: 8, minute: 0, plan: 2 },
  ],
};

function getDayType(date: Date): DayType {
  const day = date.getDay();
  if (day === 0) return 'sunday';
  if (day === 6) return 'saturday';
  return 'weekday';
}

function timeToMinutes(hour: number, minute: number): number {
  return hour * 60 + minute;
}

export interface ActivePlan {
  config: PlanConfig;
  nextChange: { hour: number; minute: number; plan: number } | null;
}

/**
 * Determine which plan is active at a given date/time.
 * Scans the schedule backwards from current time to find the matching entry.
 */
export function getActivePlan(date: Date): ActivePlan {
  const dayType = getDayType(date);
  const entries = SCHEDULE[dayType];
  const nowMinutes = timeToMinutes(date.getHours(), date.getMinutes());

  // Find active entry (last entry whose time <= now)
  let activeIdx = 0;
  for (let i = entries.length - 1; i >= 0; i--) {
    if (timeToMinutes(entries[i].hour, entries[i].minute) <= nowMinutes) {
      activeIdx = i;
      break;
    }
  }

  const activePlan = entries[activeIdx].plan;
  const config = PLAN_CONFIGS[activePlan];

  // Next change: next entry in today's schedule, or null if last
  const nextEntry = activeIdx < entries.length - 1 ? entries[activeIdx + 1] : null;

  return {
    config,
    nextChange: nextEntry ? { hour: nextEntry.hour, minute: nextEntry.minute, plan: nextEntry.plan } : null,
  };
}
